import { motion } from "framer-motion";
import { ArrowRight, MessageSquare, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1579847188804-ecba0e2ea330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwd29ya2VycyUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4ODA3NTUxNXww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#090D16]/85" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-rose-400">
            <Sparkles className="h-3.5 w-3.5" />
            {t.hero.badge}
          </span>
          <h1 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t.hero.title}{" "}
            <span className="text-rose-500">{t.hero.titleHighlight}</span>
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">
            {t.hero.subtitle}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" data-testid="hero-enquire-button" className="gap-2">
              <a href="#contact">
                {t.hero.enquire}
                <MessageSquare className="h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="hero-explore-button"
              className="gap-2 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <a href="#services">
                {t.hero.explore}
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-16 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/15 pt-8"
          data-testid="hero-stats-bar"
        >
          {t.hero.stats.map((s) => (
            <div key={s.label}>
              <div className="font-heading text-2xl font-extrabold text-amber-500 sm:text-3xl">
                {s.value}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-widest text-slate-400 sm:text-xs">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
